/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./ExperiencePassesScreen.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  CALENDAR_RESERVATION_TYPE,
  DATE_FORMAT,
  EXPERIENCE_TYPE,
} from "utils/constants";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import locationIcon from "../../assets/images/calendar-location-icon.svg";
import eventVideoIcon from "../../assets/images/event-video-icon.svg";
import appIcon from "../../assets/images/header-sabehub-app-icon.svg";
import appTextIcon from "../../assets/images/header-sabehub-text-app-icon.svg";
import bannerImage from "../../assets/images/deeplink-images/deeplink-focus-house-image.svg";
import fillingFastIcon from "../../assets/images/deeplink-images/deeplink-filling-fast-icon.svg";
import starIcon from "../../assets/images/communicate-star-icon.svg";
import arrowIcon from "../../assets/images/product-seat-left-arrow.svg";
import DeeplinkLoginDialog from "components/elements/DeeplinkLoginDialog";
import ROUTE_PATHS from "utils/routePaths";
import { getWorkspaceSlotTime } from "utils/utils";
import useWSMApi from "services/api/useWSMApi";
import { useStorage } from "services/StorageProvider";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";

const ExperiencePassesScreen: FC = () => {
  const { fetchReservations, fetchExperiencePasses, fetchProduct } =
    useWSMApi();
  const { userProfileData } = useStorage();
  const navigate = useNavigate();
  const { type } = useParams();
  const experienceType = type?.toUpperCase() ?? "";
  const [showDeeplinkLoginDialog, setShowDeeplinkLoginDialog] =
    useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);

  const { data: productData } = useQuery({
    queryKey: ["get-product-data", experienceType],
    queryFn: () => fetchProduct(experienceType),
    enabled: !!experienceType,
  });

  const { data: slotsData } = useQuery({
    queryKey: ["get-wsm-slots", productData],
    queryFn: () =>
      fetchExperiencePasses({ experience_type: productData?.experience_type }),
    enabled: !!productData,
  });

  const { data: reservationsData, isError: reservationError } = useQuery({
    queryKey: ["fetch-user-reservations"],
    queryFn: () => fetchReservations(),
    enabled: !!userProfileData,
  });

  useEffect(() => {
    if (experienceType) {
      const isLoggedIn = !!localStorage.getItem("userProfileData");
      if (!isLoggedIn) {
        navigate(ROUTE_PATHS.WSM_SLOTS_LOGIN_SCREEN, {
          state: {
            experience_type: experienceType,
          },
        });
      }
    }
  }, [experienceType]);

  const handleOnCompleteLogin = () => {
    setShowDeeplinkLoginDialog(false);
  };

  const handleBookingCardClick = (slot: any, reservation: any) => {
    if (reservation) return;
    setSelectedSlot((prev: any) => (prev?.id === slot?.id ? null : slot));
  };

  const handleContinueClick = () => {
    if (!selectedSlot) return;
    localStorage.setItem("selectedSlotData", JSON.stringify(selectedSlot));
    navigate(ROUTE_PATHS.WSM_SLOTS_PAYMENT_SCREEN);
  };

  return (
    <div className="wms-mobile-container">
      <div className="experience-passes-screen-container">
        <div className="experience-passes-header-container">
          <div className="left-container">
            <img src={appIcon} alt="header-app-icon" className="app-image" />
            <img src={appTextIcon} alt="header-text-app-icon" />
          </div>
          <div className="right-container">
            <img src={starIcon} alt="star-icon" />
            {productData?.tag}
          </div>
        </div>
        <div className="experience-passes-banner-image-container">
          <img src={bannerImage} alt="banner-image" />
        </div>
        <div className="experience-passes-page-title-container">
          <span>{productData?.title}</span>
          <span className="short-description">{productData?.description}</span>
        </div>
        <div className="experience-passes-filling-fast-container">
          <div className="left-container">
            <img src={fillingFastIcon} alt="filling-fast-icon" />
            <span>Filling Fast</span>
          </div>
          <img src={arrowIcon} alt="filling-fast-arrow-icon" />
          <div className="right-container">
            <span className="seates">
              {(productData?.total_seats ?? 0) -
                (productData?.reserved_seats ?? 0)}
            </span>
            <span>SEATS LEFT</span>
          </div>
        </div>
        <div className="experience-passes-cell-bg-container">
          <span className="title">
            Choose <span style={{ color: "#FF6B35" }}>UR Day</span>
          </span>
          <span className="sub-title">
            Pick the day you'll do your deepest work.
          </span>
          <div className="divider" />
          {slotsData?.data?.content?.map((slot: any, index: number) => {
            const reservation = !reservationError
              ? reservationsData?.data?.content?.find(
                  (reservation: any) =>
                    reservation?.service_slot_id === slot?.id,
                )
              : null;
            const isReserved = !!reservation;
            const isSelected = selectedSlot?.id === slot?.id;
            const slotTime = getWorkspaceSlotTime(slot?.start_time);

            return (
              <div key={index}>
                <div
                  className="experience-pass-list-card"
                  onClick={() => handleBookingCardClick(slot, reservation)}
                >
                  <div className="experience-pass-info-main-container">
                    <div className="experience-pass-image-container">
                      <div className="experience-pass-time">{slotTime}</div>
                      <div
                        className="experience-pass-image"
                        style={{ backgroundImage: `url(${slot?.slot_image})` }}
                      >
                        {slot?.event_type &&
                          slot?.service_type ===
                            CALENDAR_RESERVATION_TYPE.EVENTS && (
                            <div
                              className={`experience-pass-image-footer ${
                                slot?.event_type === "IN_PERSON"
                                  ? "in-person"
                                  : "in-video"
                              }`}
                            >
                              {slot?.event_type === "IN_PERSON" ? (
                                "In Person"
                              ) : (
                                <img
                                  src={eventVideoIcon}
                                  alt="calendar-in-person-icon"
                                />
                              )}
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="experience-pass-card-text-container">
                      <div style={{ width: "100%" }}>
                        <div className="date-container">
                          {dayjs(slot?.start_time).format(DATE_FORMAT)}
                        </div>
                        <div className="location-container">
                          <img src={locationIcon} alt="location-logo" />
                          {slot?.location}
                        </div>
                      </div>

                      <button
                        className={`${
                          isReserved
                            ? "experience-pass-reserved-btn"
                            : isSelected
                              ? "experience-pass-selected-btn"
                              : "experience-pass-reserve-btn"
                        }`}
                        disabled={isReserved}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleBookingCardClick(slot, reservation);
                        }}
                      >
                        {isReserved
                          ? "Reserved ✓"
                          : isSelected
                            ? "Selected ✓"
                            : "Select"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="divider" />
              </div>
            );
          })}
        </div>
        <div
          className="experience-pass-hold-button"
          onClick={handleContinueClick}
          style={{
            backgroundColor: selectedSlot ? "#C1F700" : "#A3A3AA",
            cursor: selectedSlot ? "pointer" : "not-allowed",
          }}
        >
          Hold This Day
          <img
            src={letsDoItIcon}
            className="signin-otp-view-arrow-icon"
            alt="logo"
          />
        </div>
        <span className="experience-pass-footer-tagline">
          Powered By{" "}
          <span style={{ color: "#9B5DE5" }}>{productData?.powered_by}</span>
        </span>
      </div>
      <DeeplinkLoginDialog
        open={showDeeplinkLoginDialog}
        isFocusHousePass={experienceType === EXPERIENCE_TYPE.FOCUS_HOUSE}
        onClose={() => setShowDeeplinkLoginDialog(false)}
        onCompleteLogin={handleOnCompleteLogin}
      />
    </div>
  );
};

export default ExperiencePassesScreen;
